import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import Button from '@mui/material/Button';
import { List, fromJS } from 'immutable';
import _ from 'lodash';
import React from 'react';

// eslint-disable-next-line no-restricted-imports -- pre-existing DataTable2 usage; migrate off when touched
import DataTable from '@sophocles/components/DataTable2';
import { firmwareVersionComparator } from '@sophocles/utils';
import { buildModelNameResolver } from '@sophocles/views/admin/engineering/firmwareUpgrades/firmwareUpgradeHelpers';
import { determineIsODLEngineering } from '@sophocles/utils/userPermissionUtils';
import FirmwareDialog from '@sophocles/views/admin/engineering/firmwareMatrix/dialogs/FirmwareDialog';

class FirmwareMatrixTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      data: List(),
      editFirmwareVersion: null,
      addFirmwareVersion: null,
      errors: null,
    };
  }

  closeDialog = () => this.setState({ editFirmwareVersion: null, addFirmwareVersion: null });

  clearErrors = () => this.setState({ errors: null });

  openEditDialog = editFirmwareVersion => this.setState({ editFirmwareVersion });

  openNewDialog = () => this.setState({ addFirmwareVersion: true });

  patchFirmware = (firmware_id, values) => {
    const { patchRelayControllerFirmwareVersion, setLoading } = this.props;
    setLoading(true);
    patchRelayControllerFirmwareVersion(firmware_id, values).then(res => {
      setLoading(false);
      if (_.has(res, 'error')) {
        alert('error saving firmware.');
      } else {
        // good to go, re fetch data and close dialog
        this.closeDialog();
        this._fetchData();
      }
    });
  };

  createFirmware = (firmware_id, values) => {
    const { createRelayControllerFirmwareVersion, setLoading } = this.props;
    setLoading(true);
    createRelayControllerFirmwareVersion(values).then(res => {
      setLoading(false);
      if (_.has(res, 'error')) {
        _.has(res.error, 'errors') ? this.setState({ errors: res.error.errors }) : alert('error saving firmware.');
      } else {
        // good to go, re fetch data and close dialog
        this.closeDialog();
        this._fetchData();
      }
    });
  };

  _fetchData = () => {
    const { fetchData, setLoading } = this.props;
    setLoading(true);
    fetchData().then(res => {
      setLoading(false);
      if (_.has(res, 'error')) {
        // fail out
        alert('error while attempting to fetch firmware versions');
      } else {
        this.setState({ data: res.map(row => row.set('active', row.get('obsolete', false) ? 'No' : 'Yes')) });
      }
    });
  };

  componentDidMount() {
    return this._fetchData();
  }

  render() {
    const { user, unitModels } = this.props;
    const resolveModelName = buildModelNameResolver(unitModels);
    const { data, editFirmwareVersion, addFirmwareVersion } = this.state;

    const isSuperUser = user.get('is_superuser', false);
    const groups = user.get('groups', []);

    const canEdit = isSuperUser || determineIsODLEngineering(groups);

    let columns = fromJS([
      {
        name: 'Version',
        id: 'version',
        style: { width: '100px' },
        sortComparator: firmwareVersionComparator,
      },
      {
        name: 'Release Notes',
        id: 'release_notes',
        style: { minWidth: '100px' },
        sortable: false,
        cellRenderer: value => (
          <span>
            {value.split('\n').map((item, key) => (
              <span key={key}>
                {item}
                <br />
              </span>
            ))}
          </span>
        ),
      },
      {
        name: 'Upgrade File',
        id: 'upgradeFilename',
        style: { width: '200px' },
        cellRenderer: (value, row) =>
          value ? (
            <a target="_blank" href={row.get('upgrade_file')} rel="noreferrer">
              {value}
            </a>
          ) : (
            'No File'
          ),
      },
      {
        // Derived server-side from the version and read-only here. An empty
        // list means the rule matched no models, which the picker treats as
        // unknown compatibility rather than as "fits nothing".
        name: 'Models',
        id: 'compatible_model_ids',
        style: { width: '180px' },
        sortable: false,
        cellRenderer: value => {
          const ids = value && typeof value.toJS === 'function' ? value.toJS() : value || [];
          return ids.length === 0 ? 'Unknown' : ids.map(resolveModelName).join(', ');
        },
      },
      {
        name: 'Active',
        id: 'active',
        param: 'obsolete',
        style: { width: '60px' },
      },
      {
        name: 'Testing',
        id: 'in_testing',
        param: 'in_testing',
        style: { width: '60px' },
        cellRenderer: value => (value ? 'Yes' : 'No'),
      },
    ]);

    if (canEdit) {
      columns = columns.push(
        fromJS({
          name: 'Edit',
          id: 'id',
          sortable: false,
          style: { width: '60px' },
          cellRenderer: (value, row) => (
            <Button
              color="primary"
              variant="contained"
              size="small"
              startIcon={<EditIcon fontSize="small" />}
              onClick={() => this.openEditDialog(row)}
            >
              Edit
            </Button>
          ),
        }),
      );
    }

    const modifiedData = data.map(value => {
      const filename = value.get('upgrade_file') ? value.get('upgrade_file').split('/').pop().split('?')[0] : null;
      return value.set('upgradeFilename', filename);
    });

    return (
      <div style={{ padding: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          {canEdit && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-end',
                paddingBottom: '8px',
              }}
            >
              <Button onClick={this.openNewDialog} color="primary" startIcon={<AddIcon />}>
                Add New Firmware
              </Button>
            </div>
          )}
        </div>
        {/* Table with pagination here */}
        <DataTable
          columns={columns}
          data={modifiedData}
          searchFields={['version', 'release_notes', 'active', 'upgrade_file']}
          // fetchData={this.getFetchFunction()}
          defaultSortBy="version"
          entryCountOptions={[10, 25, 50, -1]}
          defaultEntryCount={10}
        />
        {!!editFirmwareVersion && (
          <FirmwareDialog
            open={true}
            closeDialog={this.closeDialog}
            firmware={editFirmwareVersion}
            save={this.patchFirmware}
            edit="true"
          />
        )}
        {addFirmwareVersion && (
          <FirmwareDialog
            open={true}
            closeDialog={this.closeDialog}
            firmware={null}
            save={this.createFirmware}
            errors={this.state.errors}
            clearErrors={this.clearErrors}
          />
        )}
      </div>
    );
  }
}

export default FirmwareMatrixTable;
